// @flow

import React, {Fragment} from 'react';
import {fabric} from 'fabric';

import {BieBackDrop} from './BieBackDrop';
import {BieToolbar} from './BieToolbar';
import {BieConnector} from './BieConnector';
import {BieCanvas} from './BieCanvas';
import {BieLoader} from './BieLoader';
import {BieFader} from './BieFader';
import {BieModal} from './BieModal';

import {onLoadImage, getImageExtension} from './utils';
import {CanvasManager} from './managers/canvas-manager';
import {defaultPlugins} from './plugins';
import type {BiePluginType, ActionButtonType} from './types';

type Props = {
  show: boolean,
  onClose: () => void,
  onExited?: () => void,
  onError: (error: any) => void,
  onSave: (any) => void,
  saveOptions: {
    format: 'jpeg' | 'png',
    quality: number,
    multiplier: number,
    enableRetinaScaling: boolean,
  },
  image: string,
  plugins: Array<Class<BiePluginType>>,
};

type State = {
  canvasManager?: CanvasManager,
  actionButtons: Array<ActionButtonType>,
  imageStatus: 'loading' | 'loaded' | 'error',
  errorMessage: undefined,
};

export class BasicImageEditor extends React.Component<Props, State> {
  state = {
    actionButtons: [],
    canvasManager: undefined,
    imageStatus: 'loading',
    errorMessage: undefined,
  };

  static defaultProps = {
    plugins: defaultPlugins,
    saveOptions: {},
  };

  errorPromise: Promise<any>;
  errorPromiseResolver = (any) => any;

  constructor(props: Props) {
    super(props);

    this.errorPromise = new Promise(
      (resolve) => (this.errorPromiseResolver = resolve)
    );
  }

  setupCanvas = (canvasManager: CanvasManager) => {
    const image = new Image();
    image.crossOrigin = 'Anonymous';
    image.src = this.props.image;

    this.setupPlugins(canvasManager);
    this.setState({canvasManager});

    onLoadImage(image).then(
      (image) => {
        canvasManager.setImage(new fabric.Image(image));
        this.setState({imageStatus: 'loaded'});
      },
      () => {
        this.setState({
          imageStatus: 'error',
          errorMessage: "The selected image couldn't be loaded.",
        });
        this.sendError(new Error('[basic-image-editor] Error loading image'));
      }
    );
  };

  removeImage = () => {
    const {onExited} = this.props;
    this.setState({imageLoaded: 'loading'});

    if (onExited != null) {
      onExited();
    }
  };

  setupPlugins(cm: CanvasManager) {
    const {plugins} = this.props;

    const actionButtons = plugins.reduce((arr, Plugin) => {
      const plugin = new Plugin();
      plugin.setup(cm);

      return arr.concat(plugin.getActionButtons());
    }, []);

    this.setState({actionButtons});
  }

  exportImage = () => {
    const {canvasManager} = this.state;
    const {onSave, saveOptions} = this.props;

    if (canvasManager != null) {
      onSave(
        canvasManager.canvas.toDataURL({
          // export to original image type
          format:
            getImageExtension(this.props.image) === 'jpeg' ? 'jpeg' : 'png',
          // export image to its original size
          multiplier: 1 / canvasManager.canvas.getZoom(),
          ...saveOptions,
        })
      );
    }
  };

  componentDidCatch(error: any) {
    this.setState({
      imageStatus: 'error',
      errorMessage:
        'An error has occurred in the image editor and it had to be closed',
    });
    this.sendError(error);
  }

  sendError(err) {
    this.props.onError(err, this.errorPromise);
  }

  render() {
    const {show, onClose} = this.props;
    const {canvasManager, actionButtons, imageStatus, errorMessage} =
      this.state;

    return (
      <BieFader onExited={this.removeImage} in={show}>
        <BieBackDrop>
          {imageStatus !== 'error' ? (
            <Fragment>
              <BieLoader className={imageStatus} />
              <BieConnector
                className={imageStatus}
                canvas={<BieCanvas onReady={this.setupCanvas} />}
                toolbar={
                  canvasManager != null ? (
                    <BieToolbar
                      actionButtons={actionButtons}
                      canvasManager={canvasManager}
                      onClose={onClose}
                      onSave={this.exportImage}
                    />
                  ) : null
                }
              />
            </Fragment>
          ) : (
            <BieModal onClose={this.errorPromiseResolver} title="Error">
              {errorMessage}
            </BieModal>
          )}
        </BieBackDrop>
      </BieFader>
    );
  }
}
